import {
  IGenerateSettlement,
  ISearchSettlement,
  SaveSettlementRequest,
  SavedSettlementResponse,
  SettlementBuilderResponse,
  SettlementDetailProps,
  SettlementDetailRequest,
  SettlementStatus,
  SettlementsSavedResponse,
  IExportSettlements
} from './settlement.model';
import { twApi } from '../api';
import { ShareHistory } from '../trips/trips.model';

const settlementApi = twApi.injectEndpoints({
  endpoints: (builder) => ({
    getSettlementBuilder: builder.mutation<SettlementBuilderResponse, IGenerateSettlement>({
      query: (params) => {
        let url = `settlements/search-by-period?offset=${params.offset}&limit=${params.limit}`;

        if (params.start && params.end) {
          url += `&start=${params.start}&end=${params.end}`;
        }

        return {
          url: url,
          method: 'POST',
          body: { driverIds: params.driverIds, status: params.status }
        };
      },
      transformResponse: (response: { data: SettlementBuilderResponse }) => {
        return response.data;
      }
    }),
    getSettlementDetail: builder.mutation<SettlementDetailProps, SettlementDetailRequest>({
      query: ({ params, driverId }) => {
        return {
          url: `settlements/get-settlement-detail?start=${params.start}&end=${params.end}`,
          method: 'POST',
          body: { driverId: driverId }
        };
      },
      transformResponse: (response: { data: SettlementDetailProps }) => {
        return response.data;
      }
    }),
    saveSettlementDraft: builder.mutation<any, SaveSettlementRequest>({
      query: (params) => {
        return {
          url: `settlements/save-settlement`,
          method: 'POST',
          body: params
        };
      }
    }),
    deleteSettlement: builder.mutation<string, string>({
      query: (settlementId) => {
        return {
          url: `settlements/${settlementId}`,
          method: 'DELETE'
        };
      }
    }),
    getSavedSettlementDetail: builder.query<SavedSettlementResponse, string>({
      query: (settlementId) => {
        return {
          url: `settlements/${settlementId}`,
          method: 'GET'
        };
      },
      transformResponse: (response: { data: SavedSettlementResponse }) => {
        return response.data;
      }
    }),
    updateSavedSettlement: builder.mutation<any, { id: string; body: SaveSettlementRequest }>({
      query: (params) => {
        const { id, body } = params;
        return {
          url: `settlements/${id}`,
          method: 'PUT',
          body: body
        };
      }
    }),
    searchSavedSettlements: builder.mutation<
      { total: number; data: SettlementsSavedResponse },
      ISearchSettlement
    >({
      query: (params) => {
        const { startDate, endDate, offset, limit, body, includeLegacy } = params;

        let url = `settlements/search-saved-settlements?offset=${offset}&limit=${limit}`;
        if (startDate) {
          url += `&start=${startDate}`;
        }
        if (endDate) {
          url += `&end=${endDate}`;
        }
        if (includeLegacy) {
          url += `&includeLegacy=${includeLegacy}`;
        }
        return {
          url: url,
          method: 'POST',
          body
        };
      },
      transformResponse: (response: { total: number; data: SettlementsSavedResponse }) => {
        return { data: response.data, total: response.total };
      }
    }),
    getSettlementPdf: builder.mutation<Blob, string>({
      query: (settlementId) => {
        return {
          url: `settlements/get-pdf/${settlementId}`,
          method: 'GET',
          responseHandler: (response): any => response.blob()
        };
      },
      transformResponse: (response: any) => {
        return new Blob([response]);
      }
    }),
    sendSettlementEmail: builder.mutation<any, { settlementId: string; emailAddresses: string[] }>({
      query: (params) => {
        const { settlementId, emailAddresses } = params;
        return {
          url: `settlements/${settlementId}/send-email`,
          method: 'POST',
          body: emailAddresses
        };
      }
    }),
    getSettlementShareHistory: builder.query<ShareHistory[], string>({
      query: (settlementId) => ({
        url: `settlements/${settlementId}/share-history`,
        method: 'GET'
      }),
      transformResponse: (response: { data: ShareHistory[] }) => {
        return response.data;
      }
    }),
    bulkMarkSettlementsAsPaid: builder.mutation<
      string,
      { settlementIds: string[]; status: SettlementStatus }
    >({
      query: (data) => {
        return {
          url: `settlements/bulk-update-settlement-status`,
          method: 'POST',
          body: data
        };
      },
      invalidatesTags: (result, error, data) => [
        { type: 'OtherExpenses', id: 'OTHER_EXPENSES_LIST' }
      ]
    }),
    deleteSettlementAdjustmentsDefaults: builder.mutation<any, string>({
      query: (driverId) => {
        return {
          url: `settlements/delete-adjustments-defaults/${driverId}`,
          method: 'DELETE'
        };
      }
    }),
    deleteSettlementOtherPayDefaults: builder.mutation<any, string>({
      query: (driverId) => {
        return {
          url: `settlements/delete-other-pay-defaults/${driverId}`,
          method: 'DELETE'
        };
      }
    }),
    exportSettlementsCSV: builder.mutation<Blob, IExportSettlements>({
      query: (params) => {
        const { startDate, endDate, body, includeLegacy } = params;

        let url = `settlements/export-csv`;

        if (startDate && endDate) {
          url += `?start=${startDate}`;
          url += `&end=${endDate}`;
        }
        if (includeLegacy) {
          if (startDate && endDate) {
            url += `&`;
          } else {
            url += `?`;
          }
          url += `includeLegacy=${includeLegacy}`;
        }

        return {
          url: url,
          method: 'POST',
          body: body,
          responseHandler: (response): any => response.blob()
        };
      },
      transformResponse: (response: any) => {
        return new Blob([response]);
      }
    }),
    generateAndStorePdf: builder.mutation<any, string>({
      query: (settlementId) => {
        return {
          url: `settlements/generate-and-store-pdf/${settlementId}`,
          method: 'POST'
        };
      }
    })
  }),
  overrideExisting: false
});

export const {
  useGetSettlementBuilderMutation,
  useGetSettlementDetailMutation,
  useSaveSettlementDraftMutation,
  useDeleteSettlementMutation,
  useLazyGetSavedSettlementDetailQuery,
  useUpdateSavedSettlementMutation,
  useSearchSavedSettlementsMutation,
  useGetSettlementPdfMutation,
  useSendSettlementEmailMutation,
  useLazyGetSettlementShareHistoryQuery,
  useBulkMarkSettlementsAsPaidMutation,
  useDeleteSettlementAdjustmentsDefaultsMutation,
  useDeleteSettlementOtherPayDefaultsMutation,
  useExportSettlementsCSVMutation,
  useGenerateAndStorePdfMutation
} = settlementApi;
